import React, { useState, useEffect } from 'react';
import { cobrettisAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, Copy, Eye, Save, X, FileText, Mail, AlertTriangle } from 'lucide-react';

const TIPOS_PLANTILLA = [
  { value: 'RECLAMO_1', label: '1ª Reclamación' },
  { value: 'RECLAMO_2', label: '2ª Reclamación' },
  { value: 'RECLAMO_3', label: '3ª Reclamación' },
  { value: 'BUROFAX', label: 'Burofax' },
  { value: 'DEMANDA', label: 'Pre-Demanda' },
  { value: 'PLAN_PAGOS', label: 'Plan de Pagos' },
  { value: 'CUOTA_IMPAGADA', label: 'Cuota Impagada' },
  { value: 'OTRO', label: 'Otro' },
];

const VARIABLES_DISPONIBLES = [
  { var: '{{deudor_nombre}}', desc: 'Nombre completo del deudor' },
  { var: '{{deudor_email}}', desc: 'Email del deudor' },
  { var: '{{expediente_numero}}', desc: 'Número de expediente' },
  { var: '{{importe_total}}', desc: 'Importe total de la deuda' },
  { var: '{{importe_pendiente}}', desc: 'Importe pendiente' },
  { var: '{{fecha_actual}}', desc: 'Fecha actual' },
  { var: '{{fecha_vencimiento}}', desc: 'Fecha de vencimiento' },
  { var: '{{concepto}}', desc: 'Concepto de la deuda' },
  { var: '{{ronda_actual}}', desc: 'Número de ronda actual' },
  { var: '{{empresa_nombre}}', desc: 'Nombre de la empresa' },
];

export default function ConfiguracionPlantillasCobrettis() {
  const { toast } = useToast();
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewContent, setPreviewContent] = useState({ asunto: '', cuerpo: '' });
  
  const [form, setForm] = useState({
    nombre: '',
    tipo: 'RECLAMO_1',
    asunto_template: '',
    cuerpo_template: '',
    activa: true,
    orden: 0
  });

  useEffect(() => {
    loadPlantillas();
  }, []);

  const loadPlantillas = async () => {
    try {
      setLoading(true);
      const response = await cobrettisAPI.getPlantillas();
      setPlantillas(response.data || []);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las plantillas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleNueva = () => {
    setEditingId(null);
    setForm({
      nombre: '',
      tipo: 'RECLAMO_1',
      asunto_template: '',
      cuerpo_template: '',
      activa: true,
      orden: 0
    });
    setShowModal(true);
  };

  const handleEditar = (plantilla) => {
    setEditingId(plantilla.id);
    setForm({
      nombre: plantilla.nombre || '',
      tipo: plantilla.tipo || 'RECLAMO_1',
      asunto_template: plantilla.asunto_template || '',
      cuerpo_template: plantilla.cuerpo_template || '',
      activa: plantilla.activa !== false,
      orden: plantilla.orden || 0
    });
    setShowModal(true);
  };

  const handleDuplicar = (plantilla) => {
    setEditingId(null);
    setForm({
      nombre: `${plantilla.nombre} (copia)`,
      tipo: plantilla.tipo || 'RECLAMO_1',
      asunto_template: plantilla.asunto_template || '',
      cuerpo_template: plantilla.cuerpo_template || '',
      activa: true,
      orden: plantilla.orden || 0
    });
    setShowModal(true);
  };

  const handleGuardar = async () => {
    if (!form.nombre.trim()) {
      toast({ title: 'Error', description: 'El nombre es obligatorio', variant: 'destructive' });
      return;
    }
    if (!form.asunto_template.trim()) {
      toast({ title: 'Error', description: 'El asunto es obligatorio', variant: 'destructive' });
      return;
    }
    if (!form.cuerpo_template.trim()) {
      toast({ title: 'Error', description: 'El cuerpo es obligatorio', variant: 'destructive' });
      return;
    }

    try {
      if (editingId) {
        await cobrettisAPI.actualizarPlantilla(editingId, form);
        toast({ title: 'Éxito', description: 'Plantilla actualizada correctamente' });
      } else {
        await cobrettisAPI.crearPlantilla(form);
        toast({ title: 'Éxito', description: 'Plantilla creada correctamente' });
      }
      setShowModal(false);
      loadPlantillas();
    } catch (error) {
      console.error('Error guardando:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'No se pudo guardar la plantilla', 
        variant: 'destructive' 
      });
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta plantilla?')) return;
    
    try {
      await cobrettisAPI.eliminarPlantilla(id);
      toast({ title: 'Éxito', description: 'Plantilla eliminada' });
      loadPlantillas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const handlePreview = (plantilla) => {
    // Reemplazar variables con ejemplos
    let asunto = plantilla.asunto_template || '';
    let cuerpo = plantilla.cuerpo_template || '';
    
    const ejemplos = {
      '{{deudor_nombre}}': 'Juan García López',
      '{{deudor_email}}': 'juan@ejemplo.com',
      '{{expediente_numero}}': 'EXP-2024-001',
      '{{importe_total}}': '1.500,00 €',
      '{{importe_pendiente}}': '1.200,00 €',
      '{{fecha_actual}}': new Date().toLocaleDateString('es-ES'),
      '{{fecha_vencimiento}}': '15/04/2024',
      '{{concepto}}': 'Costas procesales',
      '{{ronda_actual}}': '1',
      '{{empresa_nombre}}': 'LexDuo Abogados',
    };
    
    Object.entries(ejemplos).forEach(([key, val]) => {
      asunto = asunto.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), val);
      cuerpo = cuerpo.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), val);
    });
    
    setPreviewContent({ asunto, cuerpo });
    setShowPreview(true);
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('cuerpo_template');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = form.cuerpo_template.substring(0, start) + variable + form.cuerpo_template.substring(end);
      setForm({ ...form, cuerpo_template: newValue });
    } else {
      setForm({ ...form, cuerpo_template: form.cuerpo_template + variable });
    }
  };

  const getTipoLabel = (tipo) => {
    const t = TIPOS_PLANTILLA.find(t => t.value === tipo);
    return t ? t.label : tipo;
  };

  if (loading) {
    return <div className="p-6 text-center">Cargando plantillas...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="config-plantillas-cobrettis">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Plantillas COBRETTIS</h1>
          <p className="text-gray-500">Gestiona las plantillas de email para reclamaciones de deuda</p>
        </div>
        <Button onClick={handleNueva} data-testid="btn-nueva-plantilla">
          <Plus className="h-4 w-4 mr-2" /> Nueva Plantilla
        </Button>
      </div>

      {/* Lista de plantillas */}
      {plantillas.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No hay plantillas configuradas</h3>
            <p className="text-gray-500 mb-4">Crea tu primera plantilla para comenzar a enviar reclamaciones</p>
            <Button onClick={handleNueva}>
              <Plus className="h-4 w-4 mr-2" /> Crear Plantilla
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {plantillas.map(plantilla => (
            <Card key={plantilla.id} className={!plantilla.activa ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        plantilla.tipo === 'BUROFAX' ? 'bg-red-100 text-red-700' :
                        plantilla.tipo === 'DEMANDA' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {getTipoLabel(plantilla.tipo)}
                      </span>
                      {!plantilla.activa && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">Inactiva</span>
                      )}
                    </div>
                    <h3 className="font-semibold">{plantilla.nombre}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      <strong>Asunto:</strong> {plantilla.asunto_template?.substring(0, 80)}...
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handlePreview(plantilla)} title="Vista previa">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicar(plantilla)} title="Duplicar">
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEditar(plantilla)} title="Editar">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEliminar(plantilla.id)} 
                      className="text-red-500 hover:text-red-700"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de edición */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Plantilla' : 'Nueva Plantilla'}</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Formulario */}
            <div className="space-y-4">
              <div>
                <Label>Nombre de la plantilla *</Label>
                <Input
                  value={form.nombre}
                  onChange={e => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Ej: Primera reclamación amistosa"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo *</Label>
                  <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_PLANTILLA.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Orden</Label>
                  <Input
                    type="number"
                    value={form.orden}
                    onChange={e => setForm({ ...form, orden: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Asunto del email *</Label>
                <Input
                  value={form.asunto_template}
                  onChange={e => setForm({ ...form, asunto_template: e.target.value })}
                  placeholder="Ej: Reclamación de deuda - {{expediente_numero}}"
                />
              </div>
              
              <div>
                <Label>Cuerpo del email *</Label>
                <Textarea
                  id="cuerpo_template"
                  value={form.cuerpo_template}
                  onChange={e => setForm({ ...form, cuerpo_template: e.target.value })}
                  placeholder="Escriba el contenido del email..."
                  rows={12}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.activa}
                  onCheckedChange={v => setForm({ ...form, activa: v })}
                />
                <Label>Plantilla activa</Label>
              </div>
            </div>
            
            {/* Variables disponibles */}
            <div>
              <Label className="mb-2 block">Variables disponibles</Label>
              <p className="text-xs text-gray-500 mb-3">Haz clic en una variable para insertarla en el cuerpo</p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {VARIABLES_DISPONIBLES.map(v => (
                  <div 
                    key={v.var}
                    className="p-2 border rounded cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => insertVariable(v.var)}
                  >
                    <code className="text-sm text-blue-600 font-mono">{v.var}</code>
                    <p className="text-xs text-gray-500">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleGuardar}>
              <Save className="h-4 w-4 mr-2" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de vista previa */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Vista previa del email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded">
              <Label className="text-xs text-gray-500">Asunto:</Label>
              <p className="font-medium">{previewContent.asunto}</p>
            </div>
            <div className="p-3 border rounded min-h-[200px] whitespace-pre-wrap">
              {previewContent.cuerpo}
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowPreview(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
